'use client';

import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export default function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [errors, setErrors] = useState<Partial<typeof form>>({});

  const validate = () => {
    const e: Partial<typeof form> = {};
    if (!form.name.trim()) e.name = 'Full name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email address is required';
    if (!form.message.trim() || form.message.length < 10) e.message = 'Please provide a message (at least 10 characters)';
    return e;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof form]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const supabase = createClient();
      // Insert as a service request with type "General Enquiry"
      const { error: dbError } = await supabase.from('service_requests').insert({
        client_name: form.name,
        client_email: form.email,
        client_phone: form.phone || null,
        legal_category: 'General Enquiry',
        description: `Subject: ${form.subject || '(none)'}\n\n${form.message}`,
        urgency: 'standard',
        status: 'new',
        preferred_contact: 'email',
      });
      if (dbError) throw dbError;
      setSubmitted(true);
    } catch {
      setError('There was a problem sending your message. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-24 px-6 md:px-12" aria-label="Message Sent">
        <div className="max-w-2xl mx-auto text-center border border-border bg-card p-12">
          <div className="w-10 h-10 border border-primary flex items-center justify-center mx-auto mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="text-primary" aria-hidden="true">
              <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="font-display text-2xl italic text-foreground mb-3">Message Received</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Thank you for your enquiry. A member of the team will be in touch shortly.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-6 md:px-12" aria-label="Contact Form">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-4 mb-10">
          <div className="w-8 h-px bg-primary" />
          <span className="text-xs tracking-widest uppercase text-muted-foreground">General Enquiry</span>
        </div>
        <h2 className="font-display text-3xl italic text-foreground mb-8">Send a Message</h2>

        <div className="border border-border bg-card p-1">
          <div className="border border-border/50 p-8 md:p-10">
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10">
                <div>
                  <input type="text" name="name" placeholder="Full Name *" value={form.name} onChange={handleChange} required className="form-input" autoComplete="name" aria-label="Full Name" />
                  {errors.name && <p className="text-xs text-red-400 mt-2">{errors.name}</p>}
                </div>
                <div>
                  <input type="email" name="email" placeholder="Email Address *" value={form.email} onChange={handleChange} required className="form-input" autoComplete="email" aria-label="Email Address" />
                  {errors.email && <p className="text-xs text-red-400 mt-2">{errors.email}</p>}
                </div>
                <div>
                  <input type="tel" name="phone" placeholder="Phone Number" value={form.phone} onChange={handleChange} className="form-input" autoComplete="tel" aria-label="Phone Number" />
                </div>
                <div>
                  <input type="text" name="subject" placeholder="Subject" value={form.subject} onChange={handleChange} className="form-input" aria-label="Subject" />
                </div>
              </div>
              <div className="mb-10">
                <textarea name="message" placeholder="Your Message *" value={form.message} onChange={handleChange} required rows={6} className="form-input resize-none" aria-label="Message" />
                {errors.message && <p className="text-xs text-red-400 mt-2">{errors.message}</p>}
              </div>
              {error && (
                <div className="mb-6 bg-red-950/30 border border-red-900/40 text-red-400 text-xs px-4 py-3">
                  {error}
                </div>
              )}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">
                  For legal assistance requests, please use the dedicated{' '}
                  <a href="/request-legal-assistance" className="text-primary hover:text-accent transition-colors underline">Request Legal Assistance</a> form.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full md:w-auto px-10 py-4 bg-primary hover:bg-accent text-primary-foreground text-xs font-semibold uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 group disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending…' : 'Send Message'}
                  {!submitting && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="group-hover:translate-x-1 transition-transform" aria-hidden="true">
                      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 12h14m-7-7l7 7l-7 7" />
                    </svg>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}